import { apiClient } from './apiClient';
import { LoginCredentials, RegisterData, AuthResponse, User, ChangePasswordData } from '@/types/auth';

function looksLikeHtml(data: unknown): boolean {
  if (typeof data !== 'string') return false;
  const trimmed = data.trim().toLowerCase();
  return trimmed.startsWith('<!doctype html') || trimmed.startsWith('<html');
}

function normalizeAuthResponse(data: any): AuthResponse {
  if (looksLikeHtml(data)) {
    throw new Error(
      'Login got an HTML page instead of the API. Clear site data / unregister the service worker, then reload so config.js can load the correct API URL.'
    );
  }

  const payload = data?.data && (data.accessToken || data.token) == null ? data.data : data;
  const accessToken = payload?.accessToken ?? payload?.token;
  const user = payload?.user ?? payload?.employee;

  if (!accessToken) {
    throw new Error('Login response missing access token');
  }

  return {
    accessToken,
    user,
    refreshToken: payload?.refreshToken,
  };
}

class AuthService {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await apiClient.post('/auth/login', credentials);
    return normalizeAuthResponse(response.data);
  }

  async register(data: RegisterData): Promise<{ message: string; employee: User }> {
    const response = await apiClient.post('/auth/register', data);
    return response.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get('/auth/me');
    return response.data;
  }

  async refreshToken(): Promise<{ accessToken: string }> {
    const response = await apiClient.post('/auth/refresh');
    return response.data;
  }

  async changePassword(data: ChangePasswordData): Promise<{ message: string }> {
    const response = await apiClient.post('/auth/change-password', {
      oldPassword: data.oldPassword,
      newPassword: data.newPassword,
    });
    return response.data;
  }

  async forceChangePassword(newPassword: string): Promise<{ message: string }> {
    const response = await apiClient.post('/auth/force-change-password', { newPassword });
    return response.data;
  }

  async logout(): Promise<void> {
    await apiClient.post('/auth/logout');
  }

  async forgotPassword(employeeId: string): Promise<{ message: string }> {
    const response = await apiClient.post('/auth/forgot-password', { employeeId });
    return response.data;
  }

  async impersonate(employeeId: string): Promise<AuthResponse> {
    const response = await apiClient.post('/auth/impersonate', { employeeId });
    return normalizeAuthResponse(response.data);
  }

  async issueSsoCode(payload: {
    sourceApp: 'HRMS' | 'CLUB_CORNERS';
    targetApp: 'HRMS' | 'CLUB_CORNERS';
  }): Promise<{ success: boolean; code: string; expiresIn: number; redirectUrl: string }> {
    const response = await apiClient.post('/auth/sso/issue', payload);
    return response.data;
  }

  async exchangeSsoCode(code: string): Promise<AuthResponse> {
    const response = await apiClient.post('/auth/sso/exchange', {
      code,
      targetApp: 'HRMS',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'HRMS-SSO/1.0',
    });
    return normalizeAuthResponse(response.data);
  }
}

export const authService = new AuthService(); 